import { z } from 'zod'

const fileRequired = (label: string) =>
  z.any()
    .refine((val) => val instanceof File, { message: `${label} is required` })
    .refine((val) => val instanceof File && val.size > 0, { message: `${label} cannot be empty` })

const fileOptional = z.any().optional()

/**
 * Zod validation schema for the StorageForm
 * File fields use instanceof File + size > 0 checks
 */
export const storageFormSchema = z.object({
  first_name: z.string().min(1, '*First Name is required'),
  last_name: z.string().min(1, '*Last Name is required'),
  phone: z.string().min(1, '*Phone is required'),
  email: z.string().email('*Invalid email address').min(1, '*Email is required'),
  other_phone: z.string().optional(),
  other_email: z.string().optional(),
  address_line_1: z.string().min(1, '*Address is required'),
  address_line_2: z.string().optional(),
  city: z.string().min(1, '*City is required'),
  state: z.string().min(1, '*State is required'),
  zip: z.string().min(1, '*Zip code is required'),
  emergency_contact_name: z.string().min(1, '*Emergency Contact is required'),
  emergency_contact_phone: z.string().min(1, '*Emergency Phone is required'),
  business_name: z.string().optional(),
  driver_lic_front: fileRequired("Driver's license front"),
  driver_lic_back: fileRequired("Driver's license back"),
  vehicle_type: z.string().min(1, '*Vehicle Type is required'),
  vehicle_year: z.string().min(1, '*Year is required'),
  vehicle_make: z.string().min(1, '*Make is required'),
  vehicle_model: z.string().min(1, '*Model is required'),
  vehicle_lic_state: z.string().min(1, '*License Plate State is required'),
  vehicle_lic_number: z.string().min(1, '*License Plate # is required'),
  vin: z.string().min(1, '*VIN is required'),
  description: z.string().optional(),
  proof_of_insurance: fileRequired('Proof of insurance'),
  move_in_date: z.string().min(1, '*Move-in date is required').or(z.date()),
  move_out_date: z.any().optional(),
  need_24hr_access: z.boolean(),
  need_3x_week_access: z.boolean(),
  agree_to_privacy_policy: z.boolean(),
  password: z.string().optional(),
  password_confirm: z.string().optional(),
})

export type StorageFormValues = z.infer<typeof storageFormSchema>

/**
 * Returns the list of fields to validate for a given step
 * Used for step-by-step validation in the wizard form
 */
export function getFieldsToValidateForStep(step: number, hasOAuthUser: boolean = false): (keyof StorageFormValues)[] {
  switch (step) {
    case 1:
      return hasOAuthUser
        ? ['first_name', 'last_name', 'phone']
        : ['first_name', 'last_name', 'phone', 'email']
    case 2:
      return ['address_line_1', 'city', 'state', 'zip', 'driver_lic_front', 'driver_lic_back']
    case 3:
      return ['vehicle_type', 'vehicle_year', 'vehicle_make', 'vehicle_model', 'vehicle_lic_state', 'vehicle_lic_number', 'vin', 'proof_of_insurance']
    case 4:
      return ['emergency_contact_name', 'emergency_contact_phone']
    case 5:
      return ['move_in_date']
    case 6:
      return ['agree_to_privacy_policy']
    default:
      return []
  }
}
